/**
 * Per-viewer appearance settings, held in localStorage.
 *
 * Deliberately local rather than server-side. These are personal preferences — one staff member
 * wanting a denser list or a green accent has no bearing on anyone else — and putting them in the
 * database would mean a schema, a write endpoint on a service that is otherwise read-only, and an
 * argument about who is allowed to change the look for everybody. localStorage is per-browser,
 * survives republishes, and needs none of that.
 *
 * Every read and write is wrapped: private windows, cleared site data and browsers configured to
 * block storage all make these throw rather than return null, and the page must render correctly
 * with no stored value.
 */

import { useCallback, useEffect, useState } from "react";

export type Mode = "system" | "dark" | "light";
export type Density = "compact" | "cosy" | "roomy";
export type TimeMode = "utc" | "local";

export interface Settings {
  mode: Mode;
  accent: string;
  density: Density;
  /**
   * UTC is the default and matters more than it looks. The plugin renders `/debug` output in UTC
   * (DebugLogEntry.format), and staff read both while working out what happened — two renderings of
   * one timestamp is a reliable way to make people mistrust both. Local time is offered because
   * people asked for it, and is labelled wherever it is on.
   */
  timeMode: TimeMode;
  /** Flash newly arrived rows. Separate from prefers-reduced-motion, which always wins. */
  flashNew: boolean;
  /** Show the server column in the log list. */
  showServer: boolean;
}

export const ACCENT_PRESETS: ReadonlyArray<{ name: string; value: string }> = [
  { name: "Prism violet", value: "#a78bfa" },
  { name: "Amethyst", value: "#cf91ff" },
  { name: "Emerald", value: "#5bd66a" },
  { name: "Gold", value: "#f2c230" },
  { name: "Diamond", value: "#4ee0e6" },
  { name: "Redstone", value: "#ff4d4d" },
  { name: "Ocean", value: "#4aa3f0" },
  { name: "Mint", value: "#39c9a1" },
  { name: "Lime", value: "#8fce4a" },
  { name: "Amber", value: "#e8a838" },
  { name: "Coral", value: "#f2725c" },
  { name: "Rose", value: "#f06292" },
  { name: "Slate", value: "#8896ab" },
];

const DENSITY_ROW_HEIGHT: Readonly<Record<Density, string>> = {
  compact: "24px",
  cosy: "28px",
  roomy: "34px",
};

const DEFAULTS: Settings = {
  mode: "dark",
  accent: "#a78bfa",
  density: "cosy",
  timeMode: "utc",
  flashNew: true,
  showServer: true,
};

const KEY = "goliath-staff-logs:settings";

function read(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    // Merged over the defaults so a settings object written by an older build, missing keys added
    // since, does not leave the app with undefined where it expects a value.
    const saved = { ...DEFAULTS, ...(JSON.parse(raw) as Partial<Settings>) };
    // The old default accent was saved into everyone's settings; move them onto the new theme's
    // default rather than leaving purple buttons on a green theme. A deliberate pick is kept.
    // Same for the emerald default of the previous (Minecraft) theme.
    const savedAccent = saved.accent.toLowerCase();
    if (savedAccent === "#cf91ff" || savedAccent === "#5bd66a" || savedAccent === "#8b5cf6") saved.accent = DEFAULTS.accent;
    return saved;
  } catch {
    return DEFAULTS;
  }
}

function write(settings: Settings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(settings));
  } catch {
    // Storage unavailable. The setting still applies for this page view; it just will not persist.
  }
}

/**
 * Picks black or white text for a given background, by relative luminance.
 *
 * Without this, a user choosing a pale accent gets white-on-pale buttons that cannot be read, and
 * one choosing a dark accent gets the reverse. The 0.55 threshold is tuned by eye against the preset
 * palette rather than taken from the WCAG formula — the strict 0.179 crossover puts white text on
 * several of these and it reads badly.
 */
function inkFor(hex: string): string {
  const match = /^#?([\da-f]{6})$/i.exec(hex.trim());
  if (!match) return "#120a19";
  const int = Number.parseInt(match[1] as string, 16);
  const [r, g, b] = [(int >> 16) & 255, (int >> 8) & 255, int & 255];
  const channel = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const luminance =
    0.2126 * channel(r as number) + 0.7152 * channel(g as number) + 0.0722 * channel(b as number);
  // 0.3 rather than 0.55 so the light violet default gets dark text; white on it is hard to read.
  return luminance > 0.3 ? "#1b1433" : "#ffffff";
}

export function isValidHex(value: string): boolean {
  return /^#?[\da-f]{6}$/i.test(value.trim());
}

function apply(settings: Settings): void {
  const root = document.documentElement;

  // "system" removes the attribute entirely rather than setting a value, so the prefers-color-scheme
  // media query in the stylesheet is what decides. Setting data-mode="system" would match neither of
  // the explicit selectors and silently strand the page on the dark defaults.
  if (settings.mode === "system") root.removeAttribute("data-mode");
  else root.setAttribute("data-mode", settings.mode);

  const accent = isValidHex(settings.accent) ? settings.accent : DEFAULTS.accent;
  root.style.setProperty("--accent", accent.startsWith("#") ? accent : `#${accent}`);
  root.style.setProperty("--accent-ink", inkFor(accent));
  root.style.setProperty("--row-h", DENSITY_ROW_HEIGHT[settings.density]);
}

export function useSettings(): {
  settings: Settings;
  update: (patch: Partial<Settings>) => void;
  reset: () => void;
} {
  const [settings, setSettings] = useState<Settings>(read);

  useEffect(() => {
    apply(settings);
  }, [settings]);

  const update = useCallback((patch: Partial<Settings>) => {
    setSettings((current) => {
      const next = { ...current, ...patch };
      write(next);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setSettings(DEFAULTS);
    write(DEFAULTS);
  }, []);

  return { settings, update, reset };
}

/** Applied before React mounts, so the page does not paint in the default theme and then jump. */
export function applyStoredSettingsEarly(): void {
  apply(read());
}
